import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url'
import { PDFJS_VERSION } from '../legal-templates/import/pdfImportHelpers.js'
import { MAX_FILE_BYTES, MAX_TEXT_CHARACTERS, pdfPageText } from './acervoHelpers.js'

function localUrl(value) {
  const url = new URL(value, window.location.href)
  if (url.origin !== window.location.origin || !['http:', 'https:'].includes(url.protocol))
    throw new Error('Os recursos do leitor PDF devem estar hospedados no próprio sistema.')
  return url.href
}

export async function extractPecaPdf(file, { signal, onProgress } = {}) {
  if (file.size > MAX_FILE_BYTES) throw new Error('O PDF deve ter até 10 MB.')
  let loadingTask
  let browserWorker
  let worker
  let stopped = false
  let timedOut = false
  let rejectCancellation
  const cancellation = new Promise((_, reject) => { rejectCancellation = reject })
  cancellation.catch(() => {})
  const stop = () => {
    if (stopped) return
    stopped = true
    browserWorker?.terminate()
    rejectCancellation(timedOut
      ? new Error('A leitura excedeu 60 segundos. Você pode salvar o original e informar o texto depois.')
      : new DOMException('Leitura cancelada.', 'AbortError'))
  }
  const timer = setTimeout(() => { timedOut = true; stop() }, 60000)
  signal?.addEventListener('abort', stop, { once: true })
  if (signal?.aborted) stop()
  const wait = (promise) => Promise.race([promise, cancellation])
  try {
    const pdfjs = await wait(import('pdfjs-dist'))
    if (pdfjs.version !== PDFJS_VERSION) throw new Error('Os recursos do leitor PDF precisam ser atualizados.')
    const data = new Uint8Array(await wait(file.arrayBuffer()))
    const assets = localUrl(`/pdfjs/${PDFJS_VERSION}/`)
    browserWorker = new Worker(localUrl(workerUrl), { type: 'module' })
    worker = new pdfjs.PDFWorker({ port: browserWorker })
    loadingTask = pdfjs.getDocument({
      data, worker, isEvalSupported: false, enableXfa: false, useWorkerFetch: false, useWasm: false,
      stopAtErrors: true, cMapUrl: `${assets}cmaps/`, cMapPacked: true,
      standardFontDataUrl: `${assets}standard_fonts/`, wasmUrl: `${assets}wasm/`, iccUrl: `${assets}iccs/`,
      disableAutoFetch: true, disableStream: true, disableRange: true, isImageDecoderSupported: false,
    })
    const pdf = await wait(loadingTask.promise)
    if (pdf.numPages > 100) throw new Error('A extração aceita até 100 páginas. Salve o original e informe um texto selecionado depois.')
    const pages = []
    let characters = 0
    let emptyPages = 0
    for (let number = 1; number <= pdf.numPages; number += 1) {
      const page = await wait(pdf.getPage(number))
      const reader = page.streamTextContent().getReader()
      try {
        const parts = []
        let chunk = await wait(reader.read())
        while (!chunk.done) {
          const { value } = chunk
          for (const item of value.items) {
            if (typeof item.str !== 'string') continue
            const part = pdfPageText([item], { trim: false })
            characters += part.length
            if (characters > MAX_TEXT_CHARACTERS) throw new Error('O texto excede 500.000 caracteres. Salve o original e informe um texto selecionado depois.')
            parts.push(part)
          }
          chunk = await wait(reader.read())
        }
        const text = parts.join('').trim()
        if (!text) emptyPages += 1
        characters += number > 1 ? 2 : 0
        if (characters > MAX_TEXT_CHARACTERS) throw new Error('O texto excede 500.000 caracteres. Salve o original e informe um texto selecionado depois.')
        pages.push(text)
        onProgress?.({ page: number, total: pdf.numPages })
      } finally { reader.cancel().catch(() => {}); page.cleanup() }
    }
    const conteudoTexto = pages.join('\n\n').trim()
    const avisos = []
    if (emptyPages) avisos.push(`${emptyPages} página(s) sem texto selecionável. A extração não faz OCR; confira o texto antes de indexar.`)
    if (!conteudoTexto) avisos.push('O original pode ser salvo agora. Adicione o texto na edição do documento antes de indexar.')
    return { conteudoTexto, avisos }
  } catch (error) {
    if (error?.name === 'PasswordException') throw new Error('O PDF está protegido por senha. Envie uma cópia sem senha ou adicione o texto após salvar.')
    if (error?.name === 'InvalidPDFException') throw new Error('Não foi possível ler o PDF. Confira se o arquivo está íntegro.')
    throw error
  } finally {
    clearTimeout(timer)
    signal?.removeEventListener('abort', stop)
    const cleanup = loadingTask?.destroy()
    cleanup?.catch(() => {})
    worker?.destroy()
    browserWorker?.terminate()
  }
}
